import { check } from './check';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ValidationResult,
  ValidationResultBasic,
  ValidationRule,
} from './types';
import { type Language } from '@funnellab/shared/src/types/directus-enums';

export type ValidationResultPosition = 'message' | 'tooltip' | 'none';

export function useValidation<T>(
  value: T | null | undefined,
  rules: ValidationRule<T>[],
  onCheckPassed: ((val: typeof value) => void) | undefined,
  validationMsgLng?: Language,
): ValidationResult {
  const initialized = useRef(false);
  const [isValid, setIsValid] = useState(true);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [errorMessageParams, setErrorMessageParams] = useState<
    (object | undefined)[]
  >([]);
  const [resultsPerItem, setResultsPerItem] = useState<ValidationResultBasic[]>(
    [],
  );

  const { t } = useTranslation('formValidation', {
    lng: validationMsgLng,
  });

  useEffect(() => {
    const { isValid, errorMessages, errorMessageParams, resultsPerItem } =
      check(t, value, rules);
    if (initialized.current && isValid && onCheckPassed) {
      onCheckPassed(value);
    }
    setIsValid(isValid);
    setErrorMessages(errorMessages);
    setErrorMessageParams(errorMessageParams);
    setResultsPerItem(resultsPerItem);
    initialized.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value), JSON.stringify(rules)]); // as value could be an array, we need to prevent endless loops (see https://stackoverflow.com/questions/59467758/passing-array-to-useeffect-dependency-list)

  return {
    isValid,
    errorMessages,
    errorMessageParams,
    resultsPerItem,
  };
}
